import { type CornerSize, type Island, type Panel, type Size } from '@rta/rta-blueprint-library';
import { Stack } from '@chakra-ui/react';
import { type JSX } from 'react';

import { InlineNumberInput } from '../controls';

export type IslandSizeControlsProps = {
	island: Island;
	standardSize: Size | undefined;
	cornerSize: CornerSize | undefined;
	readonly: boolean;
	controllerReadonly: boolean;
	islandCount: number;
	leftPanel: Panel | null;
	rightPanel: Panel | null;
	onStandardSizeChange: (size: Size) => void;
	onCornerSizeChange: (size: CornerSize) => void;
};

export const IslandSizeControls = (props: IslandSizeControlsProps): JSX.Element => {
	const {
		island,
		standardSize,
		cornerSize,
		readonly,
		controllerReadonly,
		islandCount,
		leftPanel,
		rightPanel,
		onStandardSizeChange,
		onCornerSizeChange
	} = props;

	const enabled = !readonly && islandCount > 0 && !island.sizeLocked;
	const heightEnabled = !controllerReadonly && islandCount > 0 && (!island.sizeLocked || island.isCornerIsland);
	const width = island.isCornerIsland ? cornerSize?.width : standardSize?.width;
	const height = island.isCornerIsland ? cornerSize?.height : standardSize?.height;

	const depthInputs = island.isCornerIsland
		? <>
			<InlineNumberInput
				label="Left Depth" min={2} max={240}
				enabled={enabled}
				value={ (leftPanel?.size.width ?? 0) + island.frontPanel.thickness}
				onChange={(value) => {
					if (!cornerSize || readonly) return;
					console.log('cornerSize', cornerSize);
					onCornerSizeChange({ ...cornerSize, depth: { ...cornerSize.depth, left: parseFloat(value) }} as CornerSize);
					island.size = { depth: { left: parseFloat(value) }};
				}}
			/>
			<InlineNumberInput
				label="Right Depth" min={2} max={240}
				enabled={enabled}
				value={ (rightPanel?.size.width ?? 0) + island.frontPanel.thickness}
				onChange={(value) => {
					if (!cornerSize || readonly) return;
					onCornerSizeChange({ depth: { ...cornerSize.depth, right: parseFloat(value) }} as CornerSize);
					island.size = { depth: { right: parseFloat(value) }};
				}}
			/>
		</>
		: <>
			<InlineNumberInput
				label="Depth" min={2} max={240}
				enabled={enabled}
				value={ standardSize?.depth ?? 0 }
				onChange={(value) => {
					if (!standardSize || readonly) return;
					onStandardSizeChange({ ...standardSize, depth: parseFloat(value) });
					island.size = { depth: parseFloat(value) };
				}}
			/>
		</>;

	return <Stack spacing="12px">
		<InlineNumberInput
			label="Width" min={2} max={300}
			enabled={enabled}
			value={ width ?? 0 }
			onChange={(value) => {
				if (readonly) return;
				if (island.isCornerIsland && cornerSize) {
					onCornerSizeChange({ ...cornerSize, width: parseFloat(value) });
					island.size = { width: parseFloat(value) };
				} else if (standardSize) {
					onStandardSizeChange({ ...standardSize, width: parseFloat(value) });
					island.size = { width: parseFloat(value) };
				}
			}}
		/>
		{ depthInputs }
		<InlineNumberInput
			label="Height" min={2} max={60}
			enabled={heightEnabled}
			value={ height ?? 0 }
			onChange={(value) => {
				if (!heightEnabled) return;
				if (island.isCornerIsland && cornerSize) {
					onCornerSizeChange({ ...cornerSize, height: parseFloat(value) });
					island.size = { height: parseFloat(value) };
				} else if (standardSize) {
					onStandardSizeChange({ ...standardSize, height: parseFloat(value) });
					island.size = { height: parseFloat(value) };
				}
			}}
		/>
	</Stack>;
};
